import appApi from "./appApi";
import {
  InstanceDataType,
  InstanceQueryArgs,
  DataExportArgs,
  InstanceType,
  InstanceMutationType,
  AddationalInstanceTypes,
} from "./api.types";

// Create a link element
const link = document.createElement("a");

const handleExportResponse = async (response: Response, exportType: string, fileName: string) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.detail || 'Export failed');
  }
  // Create a blob from the response
  const blob = await response.blob();

  // Create a URL for the blob and set it as the href attribute
  const url = window.URL.createObjectURL(blob);
  link.href = url;

  // Set the download attribute with the desired file name
  link.download = `${fileName}.${exportType.toLowerCase()}`;

  // Append the link to the document
  document.body.appendChild(link);

  // Programmatically click the link to trigger the download
  link.click();

  // Remove the link from the document
  link.remove();

  // Revoke the object URL after the download is triggered
  window.URL.revokeObjectURL(url);
}

type CustomerInstanceQueryProps = {
  debounceValue?: string;
  page: number;
  rowsPerPage?: number | null;
  ids?: number[] | null;
  version?: string;
  serviceLevel?: string;
}

const instanceApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    createInstance: build.mutation<InstanceDataType, InstanceMutationType>({
      query: (instanceData) => ({
        url: `/customer-instance/`,
        method: "POST",
        body: instanceData,
      }),
      invalidatesTags: ["Instance"],
    }),
    getInstance: build.query<
      InstanceDataType,
      CustomerInstanceQueryProps
    >({
      query: ({ debounceValue, page, rowsPerPage, ids, version = "", serviceLevel = "" }) => {
        const parameters: Record<string, string | number> = {
          service_level: serviceLevel,
          deployed_version: version,
          page,
        }
        if (ids && ids !== null) {
          parameters.id_in = ids.join(",");
        };
        if (debounceValue) {
          parameters.search = debounceValue;
        };
        if (rowsPerPage && rowsPerPage !== null) {
          parameters.page_size = rowsPerPage;
        }
        return {
          url: `/customer-instance/`,
          params: parameters,
          method: "GET"
        }
      },
      providesTags: ["Instance"],
    }),
    getInstanceById: build.query<
      InstanceType & AddationalInstanceTypes,
      string
    >({
      query: (instanceId) => `/customer-instance/${instanceId}`,
      providesTags: (result, error, id) => [{ type: "Instance", id }],
      keepUnusedDataFor: 5,
    }),
    updateRadeaInstance: build.mutation<InstanceDataType, string>({
      query: (instanceId) => ({
        url: `/customer-instance/${instanceId}/instance-status`,
        method: "GET",
      }),
      invalidatesTags: ["Instance"],
    }),
    editInstance: build.mutation<
      InstanceDataType,
      { id: string; patch: InstanceMutationType }
    >({
      query: (instanceData) => ({
        url: `/customer-instance/${instanceData.id}/`,
        method: "PATCH",
        body: instanceData.patch,
      }),
      invalidatesTags: ["Instance"],
    }),
    deleteInstance: build.mutation<null, string>({
      query: (id) => ({
        url: `/customer-instance/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Instance"],
    }),
    launchInstance: build.mutation<null, string>({
      query: (id) => ({
        url: `/customer-instance/${id}/launch/`,
        method: "POST",
      }),
      invalidatesTags: ["Instance"],
    }),
    pauseInstance: build.mutation<null, string>({
      query: (id) => ({
        url: `/customer-instance/${id}/pause/`,
        method: "POST",
      }),
      invalidatesTags: ["Instance"],
    }),
    getInstancePaginated: build.query<InstanceDataType, InstanceQueryArgs>({
      query: ({ page, rowsPerPage }) =>
        `/customer-instance/?page=${page}&page_size=${rowsPerPage}`,
      providesTags: ["Instance"],
    }),
    exportInstances: build.mutation<null, DataExportArgs>({
      query: ({ exportType }) => ({
        url: `/customer-instance/export/?type=${exportType}`,
        method: 'GET',
        responseHandler: async (response) => handleExportResponse(response, exportType, "instances"),
      }),
    }),
    exportReaderInformation: build.mutation<null, DataExportArgs>({
      query: ({ exportType }) => ({
        url: `/reader-gateway/reader-serial/export/?type=${exportType}`,
        method: 'GET',
        responseHandler: async (response) => handleExportResponse(response, exportType, "reader_information"),
      }),
    }),
    getRadeaInstanceInformation: build.mutation<null, string>({
      query: (instanceId) => ({
        url: `/customer-instance/${instanceId}/instance-information`,
        method: "GET",
      }),
      invalidatesTags: ["Instance"],
    }),
  }),
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useCreateInstanceMutation,
  useGetInstanceQuery,
  useGetInstanceByIdQuery,
  useEditInstanceMutation,
  useDeleteInstanceMutation,
  useLaunchInstanceMutation,
  usePauseInstanceMutation,
  useGetInstancePaginatedQuery,
  useUpdateRadeaInstanceMutation,
  useExportInstancesMutation,
  useExportReaderInformationMutation,
  useGetRadeaInstanceInformationMutation,
  useLazyGetInstanceQuery,
} = instanceApi;
